import { Link } from "@tanstack/react-router";
import { Mail, MapPin, Phone, Clock } from "lucide-react";
import { FacebookIcon, InstagramIcon, LinkedinIcon, TiktokIcon } from "./BrandIcons";
import { site } from "@/data/site";
import { services } from "@/data/services";
import { regions } from "@/data/regions";
import { Logo } from "./Logo";
import { partners } from "./Partners";
import { trackConversion } from "@/lib/track";

export function Footer() {
  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="container-page grid gap-10 py-14 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo inverted size="lg" />

          <p className="mt-4 text-xs font-bold uppercase tracking-[0.14em] text-brand">
            Samenwerking met
          </p>
          <ul className="mt-3 flex flex-wrap items-center gap-2">
            {partners.map((p) => (
              <li key={p.name}>
                <a
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  title={p.name}
                  aria-label={p.name}
                  className="grid h-9 w-16 place-items-center rounded-md bg-background p-1 transition hover:scale-105"
                >
                  <img
                    src={p.logo}
                    alt={`Logo ${p.name}`}
                    loading="lazy"
                    className="max-h-7 w-auto max-w-full object-contain"
                  />
                </a>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex gap-3">
            <a
              href={site.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="grid size-10 place-items-center rounded-lg bg-background p-1.5 transition hover:scale-105"
            >
              <FacebookIcon className="size-full" />
            </a>
            <a
              href={site.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="grid size-10 place-items-center rounded-lg bg-background p-1.5 transition hover:scale-105"
            >
              <InstagramIcon className="size-full" />
            </a>
            <a
              href={site.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="grid size-10 place-items-center rounded-lg bg-background p-1.5 transition hover:scale-105"
            >
              <LinkedinIcon className="size-full" />
            </a>
            <a
              href={site.social.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="grid size-10 place-items-center rounded-lg bg-background p-1.5 transition hover:scale-105"
            >
              <TiktokIcon className="size-full" />
            </a>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-brand">Diensten</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {services.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/diensten/$slug"
                  params={{ slug: s.slug }}
                  className="opacity-85 transition hover:text-brand hover:opacity-100"
                >
                  {s.title}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/vlog" className="opacity-85 transition hover:text-brand hover:opacity-100">
                Vlog: Vakkundig in actie
              </Link>
            </li>
            <li>
              <Link to="/verhuiskosten" className="opacity-85 transition hover:text-brand hover:opacity-100">
                Wat kost een verhuizing?
              </Link>
            </li>
            <li>
              <Link to="/piano-verhuizen" className="opacity-85 transition hover:text-brand hover:opacity-100">
                Piano verhuizen
              </Link>
            </li>
            <li>
              <Link to="/studentenverhuizing" className="opacity-85 transition hover:text-brand hover:opacity-100">
                Studentenverhuizing
              </Link>
            </li>
            <li>
              <Link
                to="/internationaal-verhuizen"
                className="opacity-85 transition hover:text-brand hover:opacity-100"
              >
                Internationaal verhuizen
              </Link>
            </li>
            <li>
              <Link to="/kantoorverhuizing" className="opacity-85 transition hover:text-brand hover:opacity-100">
                Kantoorverhuizing
              </Link>
            </li>
            <li>
              <Link
                to="/woningontruiming-den-haag"
                className="opacity-85 transition hover:text-brand hover:opacity-100"
              >
                Woningontruiming Den Haag
              </Link>
            </li>
            <li>
              <Link to="/spoedverhuizing" className="opacity-85 transition hover:text-brand hover:opacity-100">
                Spoedverhuizing
              </Link>
            </li>
          </ul>

        </div>

        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-brand">Werkgebied</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {regions.slice(0, 8).map((r) => (
              <li key={r.slug}>
                <Link
                  to="/werkgebied/$slug"
                  params={{ slug: r.slug }}
                  className="opacity-85 transition hover:text-brand hover:opacity-100"
                >
                  Verhuisbedrijf {r.city}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/werkgebied" className="font-semibold text-brand">
                Alle plaatsen
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-brand">Contact</h2>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex gap-2">
              <Phone className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
              <a href={site.phoneHref}
      onClick={() => trackConversion("telefoon_klik")} className="font-semibold hover:text-brand">
                {site.phone}
              </a>
            </li>
            <li className="flex gap-2">
              <Mail className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
              <a href={site.emailHref} className="break-all opacity-85 hover:text-brand">
                {site.email}
              </a>
            </li>
            <li className="flex gap-2">
              <MapPin className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
              <span className="opacity-85">
                {site.address.street}, {site.address.postalCode} {site.address.city}
              </span>
            </li>
            <li className="flex gap-2">
              <Clock className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
              <span className="opacity-85">{site.hours}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-background/10">
        <div className="container-page flex flex-col gap-2 py-5 text-xs opacity-75 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {site.name}
          </p>
          <div className="flex gap-4">
            <Link to="/algemene-voorwaarden" className="hover:text-brand">
              Algemene voorwaarden
            </Link>
            <Link to="/privacybeleid" className="hover:text-brand">
              Privacybeleid
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
